import React from 'react';
import { Brain, Loader2, Send } from 'lucide-react';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import {
  ALL_MODELS,
  supportsExtendedThinking,
  supportsWebSearch,
  useLlmTestHarnessViewModel,
} from './viewModel';
import { effortIsActive, getSupportedEffortLevels } from '../../../../domain/llm';
import type { LlmModelId, LlmStreamStatus } from '../../../../domain/llm';

const MODEL_LABELS: Record<LlmModelId, string> = {
  // Anthropic — current
  'claude-opus-5': 'Claude 5',
  'claude-opus-4-7': 'Opus 4.7',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-haiku-4-5': 'Haiku 4.5',
  // Anthropic — predecessors
  'claude-opus-4-6': 'Opus 4.6 (prev. gen.)',
  'claude-sonnet-4-5': 'Sonnet 4.5 (prev. gen.)',
  // OpenAI — current (5.6 Sol / 5.5)
  'gpt-5.6-sol': 'GPT-5.6 Sol',
  'gpt-5.5': 'GPT-5.5',
  'gpt-5.5-pro': 'GPT-5.5 Pro',
  // OpenAI — current (5.4)
  'gpt-5.4': 'GPT-5.4',
  'gpt-5.4-pro': 'GPT-5.4 Pro',
  'gpt-5.4-mini': 'GPT-5.4 Mini',
  'gpt-5.4-nano': 'GPT-5.4 Nano',
  // OpenAI — predecessor
  'gpt-5': 'GPT-5 (prev. gen.)',
  'gpt-5-mini': 'GPT-5 Mini (prev. gen.)',
  'gpt-5-nano': 'GPT-5 Nano (prev. gen.)',
  // Gemini — current (3.x)
  'gemini-3.7-flash': 'Gemini Flash 3.7',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro (preview)',
  'gemini-3-flash-preview': 'Gemini 3 Flash (preview)',
  'gemini-3.1-flash-lite': 'Gemini 3.1 Flash Lite',
  // Gemini — predecessor (deprecates 2026-06-17)
  'gemini-2.5-pro': 'Gemini 2.5 Pro (prev. gen.)',
  // Together
  'moonshotai/Kimi-K3': 'Kimi K3 (Together)',
  'zai-org/GLM-5.2': 'GLM-5.2 (Together)',
  'MiniMaxAI/MiniMax-M2.7': 'MiniMax M2.7 (Together)',
};

const STATUS_STYLES: Record<LlmStreamStatus, string> = {
  thinking: 'bg-indigo-100 text-indigo-700',
  responding: 'bg-amber-100 text-amber-700',
  done: 'bg-emerald-100 text-emerald-700',
  error: 'bg-red-100 text-red-700',
};

export function LlmTestTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const vm = useLlmTestHarnessViewModel();
  useViewModelReady(vm.initialize);

  const thinkingSupported = supportsExtendedThinking(vm.model);
  const webSearchSupported = supportsWebSearch(vm.model);
  // Show the effort picker only when effort actually reaches the wire on
  // this call — i.e. Gemini 3.x (always thinks) OR optional-thinking models
  // with the thinking checkbox currently on. See effortIsActive in
  // domain/llm for the full per-mode logic.
  const effortSupported = effortIsActive(vm.model, vm.thinkingEnabled);
  const effortLevels = getSupportedEffortLevels(vm.model) ?? [];

  return (
    <section className="max-w-5xl min-w-0">
      {vm.busy ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">LLM Test Harness</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Send a one-shot prompt to an Anthropic or Together model. Thinking + text stream
                  live; final usage and cost are written to the llm_usage collection.
                </p>
              </div>
              {mobileNavTrigger}
            </div>

            <section className="rounded-md border border-gray-200 bg-white p-4 space-y-4 sm:p-5">
              <div>
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
                  Prompt
                </label>
                <textarea
                  id="prompt"
                  value={vm.prompt}
                  onChange={(e) => vm.setPrompt(e.target.value)}
                  rows={6}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:ring-primary text-sm font-mono"
                  placeholder="Ask the model something…"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="model" className="block text-sm font-medium text-gray-700">
                    Model
                  </label>
                  <SearchableDropdown<LlmModelId>
                    id="model"
                    options={ALL_MODELS.map((model) => ({
                      value: model,
                      label: MODEL_LABELS[model],
                      searchText: `${MODEL_LABELS[model]} ${model}`,
                    }))}
                    value={vm.model}
                    onChange={(value) => {
                      if (value !== null) vm.setModel(value);
                    }}
                    searchPlaceholder="Search models"
                    preserveOrder
                    triggerClassName="mt-1 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Thinking</label>
                  <div className="mt-2 flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={vm.thinkingEnabled}
                        disabled={!thinkingSupported}
                        onChange={(e) => vm.setThinkingEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      Enabled
                    </label>
                    <input
                      type="number"
                      min={1024}
                      step={1024}
                      value={vm.thinkingBudget}
                      onChange={(e) => vm.setThinkingBudget(Number(e.target.value) || 0)}
                      disabled={!vm.thinkingEnabled}
                      className="w-28 rounded-md border border-gray-300 px-3 py-2 shadow-sm text-sm disabled:bg-gray-100"
                      placeholder="budget"
                    />
                  </div>
                  {!thinkingSupported && (vm.model === 'claude-opus-5' || vm.model === 'claude-opus-4-7') && (
                    <p className="mt-1 text-xs text-gray-400">
                      This Claude model uses adaptive thinking automatically — no toggle needed.
                    </p>
                  )}
                  {!thinkingSupported && vm.model.startsWith('gemini-3') && (
                    <p className="mt-1 text-xs text-gray-400">
                      Gemini 3.x always thinks — pick depth via effort.
                    </p>
                  )}
                </div>

                {effortSupported && (
                  <div>
                    <label htmlFor="effort" className="block text-sm font-medium text-gray-700">
                      Effort
                    </label>
                    <SearchableDropdown
                      id="effort"
                      options={effortLevels.map((level) => ({ value: level, label: level }))}
                      value={vm.effort}
                      onChange={(value) => {
                        if (value !== null) vm.setEffort(value);
                      }}
                      searchPlaceholder="Search effort levels"
                      preserveOrder
                      triggerClassName="mt-1 text-sm"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Tools</label>
                  <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={vm.webSearchEnabled}
                      disabled={!webSearchSupported}
                      onChange={(e) => vm.setWebSearchEnabled(e.target.checked)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    Web search
                  </label>
                  {!webSearchSupported && (
                    <p className="mt-1 text-xs text-gray-400">
                      Not supported on this provider yet.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => void vm.reset()}
                  className="min-h-[40px] rounded-md text-sm text-gray-500 hover:text-gray-700 sm:min-h-0"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => void vm.send()}
                  disabled={vm.sending || !vm.prompt.trim()}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {vm.sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Send
                </button>
              </div>
            </section>

            {vm.lastError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {vm.lastError}
              </div>
            )}

            {vm.streamDocId && (
              <section className="mt-6 rounded-md border border-gray-200 bg-white p-4 space-y-4 sm:p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <StatusBadge status={vm.streamDoc?.status ?? null} />
                    {vm.streamDoc?.billingStatus && (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        vm.streamDoc.billingStatus === 'recorded'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        billing {vm.streamDoc.billingStatus}
                      </span>
                    )}
                    <code className="break-all text-xs text-gray-500">{vm.streamDocId}</code>
                  </div>
                  {vm.streamDoc?.usage && <UsageReadout usage={vm.streamDoc.usage} />}
                </div>

                {vm.streamDoc?.thinking && (
                  <details open className="group">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Brain className="h-4 w-4 text-indigo-500" />
                      Thinking
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap rounded-md bg-indigo-50 p-3 text-xs font-mono text-indigo-900 max-h-96 overflow-auto">
                      {vm.streamDoc.thinking}
                    </pre>
                  </details>
                )}

                {vm.streamDoc?.text && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Response</p>
                    <pre className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm font-mono text-gray-900">
                      {vm.streamDoc.text}
                    </pre>
                  </div>
                )}

                {vm.streamDoc?.status === 'error' && vm.streamDoc.errorMessage && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {vm.streamDoc.errorMessage}
                    {vm.streamDoc.errorStatus ? ` (${vm.streamDoc.errorStatus})` : ''}
                  </div>
                )}
              </section>
            )}
          </>
        )}
    </section>
  );
}

function StatusBadge({ status }: { status: LlmStreamStatus | null }) {
  const label = status ?? 'pending';
  const cls = status ? STATUS_STYLES[status] : 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>{label}</span>
  );
}

function UsageReadout({ usage }: { usage: NonNullable<ReturnType<() => import('../../../../domain/llm').LlmStreamDoc['usage']>> }) {
  const stu = usage.serverToolUse;
  const toolParts: string[] = [];
  if (stu?.webSearchRequests) toolParts.push(`🔍 ${stu.webSearchRequests}`);
  if (stu?.webFetchRequests)  toolParts.push(`🌐 ${stu.webFetchRequests}`);
  if (stu?.codeExecutionRequests) toolParts.push(`🐍 ${stu.codeExecutionRequests}`);
  return (
    <div className="text-xs text-gray-500 font-mono">
      in: {usage.inputTokens} · out: {usage.outputTokens}
      {usage.cacheReadTokens ? ` · cache-r: ${usage.cacheReadTokens}` : ''}
      {usage.cacheWriteTokens ? ` · cache-w: ${usage.cacheWriteTokens}` : ''}
      {toolParts.length > 0 ? ` · ${toolParts.join(' ')}` : ''}
    </div>
  );
}
